import { useAdvertisementComponent } from '@/hooks/useAdvertisementComponent';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { AlertTriangle, Trophy } from 'lucide-react';
import Markdown from 'react-markdown';
import { Typography } from '@/components/ui/typography';
import { AdvertisementSkeleton } from './AdvertisementSkeleton';

interface AdvertisementComponentProps {
  isLoading?: boolean;
}

const AdvertisementComponent = ({ isLoading }: AdvertisementComponentProps) => {
  const { advertisement, advertisementColor } = useAdvertisementComponent();

  if (isLoading) {
    return <AdvertisementSkeleton />;
  }

  return (
    advertisement && (
      <Card
        className="p-6 mb-6 prose prose-blue max-w-non"
        style={{ backgroundColor: advertisementColor }}>
        {advertisement.type === 'warning' && (
          <div className="flex pb-2 text-[#D97706]">
            <AlertTriangle />
            <span className="px-2">Внимание!</span>
          </div>
        )}
        <Markdown
          components={{
            h1: ({ children }) => <h1 className="mb-4 text-2xl font-bold">{children}</h1>,
            h2: ({ children }) => <h2 className="mb-3 text-xl font-semibold">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-2 text-lg font-medium">{children}</h3>,
            p: ({ children }) => <p className="leading-relaxed text-gray-600">{children}</p>,
            ul: ({ children }) => <ul className="pl-6 space-y-2 list-disc">{children}</ul>,
            ol: ({ children }) => <ol className="pl-6 space-y-2 list-decimal">{children}</ol>,
            li: ({ children }) => <li className="text-gray-600">{children}</li>,
            a: ({ children, href }) => (
              <a href={href} className="underline text-primary hover:text-primary/80">
                {children}
              </a>
            ),
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            blockquote: ({ children }) => (
              <blockquote className="pl-4 my-4 italic text-gray-600 border-l-4 border-primary/20">
                {children}
              </blockquote>
            ),
            code: ({ children }) => (
              <code className="bg-gray-100 rounded px-1 py-0.5 text-sm">{children}</code>
            ),
          }}>
          {advertisement.content}
        </Markdown>
      </Card>
    )
  );
};

export default AdvertisementComponent;
